import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Download, DollarSign, Clock, User, CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { generate1099NecPdf } from './nec1099Pdf';

const AdminPayroll = () => {
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [generating, setGenerating] = useState(false);
  const payrollRef = useRef(null);

  useEffect(() => {
    loadPayroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const loadPayroll = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7));
      startOfWeek.setHours(0, 0, 0, 0);
      const data = await api(`/api/admin/payroll?week_start=${startOfWeek.toISOString()}`);
      setPayroll(data);
    } catch (err) {
      toast.error('Failed to load payroll');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const weekStart = payroll?.week_start ? new Date(payroll.week_start).toLocaleDateString() : '';
  const weekEnd = payroll?.week_end ? new Date(payroll.week_end).toLocaleDateString() : '';

  return (
    <div data-testid="admin-payroll" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-white">Payroll</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => setWeekOffset(w => w - 1)} className="btn-secondary text-sm" data-testid="prev-week-btn">Previous Week</button>
          <span className="text-gray-400 text-sm">{weekStart} - {weekEnd}</span>
          <button onClick={() => setWeekOffset(w => w + 1)} className="btn-secondary text-sm" data-testid="next-week-btn">Next Week</button>
          <button
            onClick={async () => {
              if (!payrollRef.current) return;
              setGenerating(true);
              try {
                const canvas = await html2canvas(payrollRef.current, { scale: 2, backgroundColor: '#0a0a0a', useCORS: true });
                const imgData = canvas.toDataURL('image/jpeg', 0.95);
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                let position = 0;
                const pageHeight = pdf.internal.pageSize.getHeight();
                while (position < pdfHeight) {
                  if (position > 0) pdf.addPage();
                  pdf.addImage(imgData, 'JPEG', 0, -position, pdfWidth, pdfHeight);
                  position += pageHeight;
                }
                pdf.save(`BELLAJA-Payroll-${weekStart}-${weekEnd}.pdf`);
                toast.success('Payroll PDF downloaded!');
              } catch { toast.error('Failed to generate PDF'); }
              finally { setGenerating(false); }
            }}
            disabled={generating}
            className="btn-primary text-sm flex items-center gap-2"
            data-testid="download-payroll-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
        </div>
      </div>

      <div ref={payrollRef}>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <p className="text-sm text-gray-400 mb-1">Total Gross Pay</p>
          <p className="text-3xl font-bold text-white" data-testid="total-gross">${payroll?.totals?.total_gross?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-400 mb-1">Total Net Pay</p>
          <p className="text-3xl font-bold text-green-400" data-testid="total-net">${payroll?.totals?.total_net?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="card p-6">
          <p className="text-sm text-gray-400 mb-1">Technicians</p>
          <p className="text-3xl font-bold text-white">{payroll?.totals?.technician_count || 0}</p>
        </div>
      </div>

      {/* Per Technician */}
      <div className="space-y-4">
        {(payroll?.technicians || []).map((tech) => (
          <div key={tech.technician_id} className="card p-6" data-testid={`payroll-tech-${tech.technician_id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-xl font-bold text-white">{tech.technician_name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold tracking-widest ${
                    tech.employment_type === '1099'
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  }`} data-testid={`tech-emp-type-${tech.technician_id}`}>
                    {tech.employment_type || 'W2'} · {tech.tax_form || 'W-2'}
                  </span>
                </div>
                <p className="text-sm text-gray-400">{tech.technician_email}</p>
                {tech.employment_type === '1099' && (
                  <p className="text-[11px] text-purple-300 mt-1" data-testid={`ytd-${tech.technician_id}`}>
                    YTD: ${tech.ytd_pay?.toFixed(2) || '0.00'} ({tech.ytd_hours?.toFixed(1) || 0}h)
                    {tech.nec_required && <span className="ml-2 bg-orange-500/30 text-orange-200 px-1.5 py-0.5 rounded text-[10px] font-bold">1099-NEC REQUIRED</span>}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm text-gray-400">Rate: ${tech.hourly_rate?.toFixed(2)}/hr</p>
                  {tech.hourly_rate === 0 && (
                    <p className="text-xs text-red-400">Rate not set!</p>
                  )}
                </div>
                <button
                  onClick={async () => {
                    try {
                      const el = document.getElementById(`payroll-card-${tech.technician_id}`);
                      if (!el) return;
                      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#0a0a0a', useCORS: true });
                      const imgData = canvas.toDataURL('image/jpeg', 0.95);
                      const pdf = new jsPDF('p', 'mm', 'a4');
                      const pdfWidth = pdf.internal.pageSize.getWidth();
                      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
                      pdf.save(`BELLAJA-Payroll-${tech.technician_name.replace(/\s/g, '_')}-${weekStart}-${weekEnd}.pdf`);
                      toast.success(`Payroll PDF for ${tech.technician_name} downloaded!`);
                    } catch { toast.error('Failed to generate PDF'); }
                  }}
                  className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5"
                  data-testid={`download-tech-payroll-${tech.technician_id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                  PDF
                </button>
                {tech.employment_type === '1099' && (tech.ytd_pay || 0) >= 600 && (
                  <button
                    onClick={async () => {
                      try {
                        await generate1099NecPdf({
                          recipient: {
                            name: tech.technician_name,
                            email: tech.technician_email,
                            address: '',  // Not stored in user model yet
                          },
                          year: new Date().getFullYear(),
                          nonemployee_compensation: tech.ytd_pay || 0,
                          federal_withheld: 0,
                          ny_state_withheld: 0,
                        });
                        toast.success('1099-NEC PDF downloaded');
                      } catch (err) { toast.error('Failed: ' + err.message); }
                    }}
                    className="text-xs bg-orange-900/40 hover:bg-orange-800/60 text-orange-300 px-3 py-1.5 rounded font-bold tracking-widest border border-orange-500/30 flex items-center gap-1.5"
                    data-testid={`download-1099nec-${tech.technician_id}`}
                    title="Generate IRS-style 1099-NEC year-end statement"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    1099-NEC
                  </button>
                )}
              </div>
            </div>
            <div id={`payroll-card-${tech.technician_id}`} className="bg-gray-950 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-800">
                <div>
                  <p className="text-lg font-bold text-white">{tech.technician_name}</p>
                  <p className="text-xs text-gray-500">{tech.technician_email} | Rate: ${tech.hourly_rate?.toFixed(2)}/hr</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">BELLAJA Electrical</p>
                  <p className="text-xs text-gray-600">Week: {weekStart} - {weekEnd}</p>
                </div>
              </div>

            {/* Hours breakdown */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-800/50 p-3 rounded">
                <p className="text-xs text-gray-400">Regular Hours</p>
                <p className="text-lg font-bold text-white">{tech.regular_hours}h</p>
                <p className="text-xs text-gray-500">${tech.regular_pay?.toFixed(2)}</p>
              </div>
              <div className="bg-gray-800/50 p-3 rounded">
                <p className="text-xs text-gray-400">Overtime Hours</p>
                <p className="text-lg font-bold text-orange-400">{tech.overtime_hours}h</p>
                <p className="text-xs text-gray-500">${tech.overtime_pay?.toFixed(2)} (1.5x)</p>
              </div>
              <div className="bg-gray-800/50 p-3 rounded">
                <p className="text-xs text-gray-400">Total Hours</p>
                <p className="text-lg font-bold text-white">{(tech.regular_hours + tech.overtime_hours).toFixed(1)}h</p>
              </div>
            </div>

            {/* Pay breakdown */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-300 mb-2">Gross Pay</p>
                <p className="text-2xl font-bold text-white">${tech.gross_pay?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-300 mb-2">Net Pay</p>
                <p className="text-2xl font-bold text-green-400">${tech.net_pay?.toFixed(2)}</p>
              </div>
            </div>

            {/* Tax Deductions */}
            {tech.gross_pay > 0 && (
              <div className="mt-4 border-t border-gray-800 pt-4">
                <p className="text-sm font-medium text-gray-300 mb-2">NYC Tax Deductions</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {tech.deductions && Object.entries(tech.deductions).map(([key, val]) => (
                    <div key={key} className="bg-gray-800/30 p-2 rounded text-center">
                      <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-medium text-red-400">-${val.amount?.toFixed(2)}</p>
                      <p className="text-xs text-gray-600">{(val.rate * 100).toFixed(2)}%</p>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-right">
                  <span className="text-sm text-gray-400">Total Deductions: </span>
                  <span className="text-sm font-bold text-red-400">-${tech.total_deductions?.toFixed(2)}</span>
                </div>
              </div>
            )}
            </div>
          </div>
        ))}

        {(payroll?.technicians || []).length === 0 && (
          <div className="card p-8 text-center text-gray-400">
            No technicians found.
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

// ===================== ADMIN SITE CONTENT (CMS) =====================


export default AdminPayroll;
